import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useThemeStore } from '../../stores/themeStore';
import { toast } from '../../stores/toastStore';

const RegisterPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { register, isLoading, clearError } = useAuthStore();
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  const passwordRules = [
    { label: '至少8个字符', test: (p: string) => p.length >= 8 },
    { label: '包含大写字母', test: (p: string) => /[A-Z]/.test(p) },
    { label: '包含小写字母', test: (p: string) => /[a-z]/.test(p) },
    { label: '包含数字', test: (p: string) => /\d/.test(p) },
    { label: '包含特殊字符', test: (p: string) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
  ];

  const passwordStrength = password
    ? passwordRules.filter((rule) => rule.test(password)).length
    : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (password !== confirmPassword) {
      toast.warning('密码不一致', '两次输入的密码不一致');
      return;
    }
    const result = await register(username, email, password, inviteCode.trim());
    if (result.success) {
      toast.success('注册成功', '欢迎加入');
      navigate('/');
    } else {
      toast.error('注册失败', result.error);
    }
  };

  const canGoNext = () => {
    if (step === 1) return /^\d{9,}$/.test(inviteCode.trim());
    if (step === 2) return username.trim().length > 0;
    if (step === 3) return email.trim().length > 0 && /\S+@\S+\.\S+/.test(email);
    return false;
  };

  return (
    <div className={`h-screen flex flex-col items-center justify-center px-6 relative ${
      isLight ? 'bg-white' : 'bg-[#060908]'
    }`}>
      <div className="w-full max-w-[320px]">
        {/* Logo */}
        <div className="flex justify-center mb-16">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
              isLight
                ? 'bg-gradient-to-br from-primary to-teal-500'
                : 'bg-gradient-to-br from-primary to-teal-400'
            }`}>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <span className={`text-base font-bold tracking-tight ${isLight ? 'text-stone-900' : 'text-white'}`}>
              AI SaaS
            </span>
          </Link>
        </div>

        {/* 步骤指示器 */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`transition-all duration-300 rounded-full ${
                s === step
                  ? `w-6 h-1.5 ${isLight ? 'bg-primary' : 'bg-primary/60'}`
                  : s < step
                    ? `w-1.5 h-1.5 ${isLight ? 'bg-primary' : 'bg-primary/60'}`
                    : `w-1.5 h-1.5 ${isLight ? 'bg-stone-200' : 'bg-white/[0.1]'}`
              }`}
            />
          ))}
        </div>

        {/* 步骤标题 */}
        <h1 className={`text-xl font-bold tracking-tight text-center mb-10 ${
          isLight ? 'text-stone-900' : 'text-white'
        }`}>
          {step === 1 && '输入邀请码'}
          {step === 2 && '创建用户名'}
          {step === 3 && '输入邮箱'}
          {step === 4 && '设置密码'}
        </h1>

        {/* 表单 */}
        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Step 1: 邀请码 */}
          <div className={`transition-all duration-500 ${
            step >= 1 ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0 overflow-hidden'
          }`}>
            <label htmlFor="register-invite-code" className="sr-only">邀请码</label>
            <input
              id="register-invite-code"
              type="text"
              inputMode="numeric"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.replace(/\D/g, ''))}
              className={`w-full px-0 py-3 text-sm border-0 border-b focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bg-transparent transition-colors duration-200 ${
                isLight
                  ? 'border-stone-200 text-stone-900 placeholder-stone-400 focus:border-primary'
                  : 'border-white/[0.1] text-stone-100 placeholder-stone-600 focus:border-primary/60'
              } ${step > 2 ? 'border-stone-400' : ''}`}
              placeholder="邀请码（至少9位数字）"
              required
              disabled={step > 1}
              autoFocus={step === 1}
            />
          </div>

          {/* Step 2: 用户名 */}
          <div className={`transition-all duration-500 ${
            step >= 2 ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0 overflow-hidden'
          }`}>
            <label htmlFor="register-username" className="sr-only">用户名</label>
            <input
              id="register-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={`w-full px-0 py-3 text-sm border-0 border-b focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bg-transparent transition-colors duration-200 ${
                isLight
                  ? 'border-stone-200 text-stone-900 placeholder-stone-400 focus:border-primary'
                  : 'border-white/[0.1] text-stone-100 placeholder-stone-600 focus:border-primary/60'
              } ${step > 1 ? 'border-stone-400' : ''}`}
              placeholder="用户名"
              required
              disabled={step > 2}
              autoFocus={step === 2}
            />
          </div>

          {/* Step 3: 邮箱 */}
          <div className={`transition-all duration-500 ${
            step >= 3 ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0 overflow-hidden'
          }`}>
            <label htmlFor="register-email" className="sr-only">邮箱</label>
            <input
              id="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-0 py-3 text-sm border-0 border-b focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bg-transparent transition-colors duration-200 ${
                isLight
                  ? 'border-stone-200 text-stone-900 placeholder-stone-400 focus:border-primary'
                  : 'border-white/[0.1] text-stone-100 placeholder-stone-600 focus:border-primary/60'
              } ${step > 3 ? 'border-stone-400' : ''}`}
              placeholder="邮箱"
              required
              disabled={step > 3}
              autoFocus={step === 3}
            />
          </div>

          {/* Step 4: 密码 */}
          <div className={`transition-all duration-500 ${
            step >= 4 ? 'opacity-100 max-h-[300px]' : 'opacity-0 max-h-0 overflow-hidden'
          }`}>
            <label htmlFor="register-password" className="sr-only">密码</label>
            <input
              id="register-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full px-0 py-3 text-sm border-0 border-b focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bg-transparent transition-colors duration-200 ${
                isLight
                  ? 'border-stone-200 text-stone-900 placeholder-stone-400 focus:border-primary'
                  : 'border-white/[0.1] text-stone-100 placeholder-stone-600 focus:border-primary/60'
              }`}
              placeholder="密码"
              required
              autoFocus={step === 4}
            />

            {/* 密码强度 - 极简指示条 */}
            {password && (
              <div className="mt-2">
                <div className={`flex gap-1 mb-3`}>
                  {[1, 2, 3, 4, 5].map((level) => (
                    <div
                      key={level}
                      className={`flex-1 h-0.5 rounded-full transition-all duration-300 ${
                        passwordStrength >= level
                          ? passwordStrength <= 1 ? 'bg-red-500'
                            : passwordStrength <= 3 ? 'bg-amber-500'
                            : 'bg-emerald-500'
                          : isLight ? 'bg-stone-100' : 'bg-white/[0.06]'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            <label htmlFor="register-confirm-password" className="sr-only">确认密码</label>
            <input
              id="register-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={`w-full px-0 py-3 text-sm border-0 border-b focus:outline-none focus:ring-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bg-transparent transition-colors duration-200 ${
                isLight
                  ? 'border-stone-200 text-stone-900 placeholder-stone-400 focus:border-primary'
                  : 'border-white/[0.1] text-stone-100 placeholder-stone-600 focus:border-primary/60'
              }`}
              placeholder="确认密码"
              required
            />
          </div>

          {/* 按钮 */}
          <div className="pt-6">
            {step < 4 ? (
              <button
                type="button"
                onClick={() => canGoNext() && setStep(step + 1)}
                disabled={!canGoNext()}
                className={`w-full py-3 rounded-lg text-sm font-semibold text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed ${
                  isLight
                    ? 'bg-primary hover:bg-primary/90'
                    : 'bg-primary/20 hover:bg-primary/30 border border-primary/30'
                }`}
              >
                继续
              </button>
            ) : (
              <button
                type="submit"
                disabled={isLoading || !password || password !== confirmPassword}
                className={`w-full py-3 rounded-lg text-sm font-semibold text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed ${
                  isLight
                    ? 'bg-primary hover:bg-primary/90'
                    : 'bg-primary/20 hover:bg-primary/30 border border-primary/30'
                }`}
              >
                {isLoading ? '注册中...' : '创建账户'}
              </button>
            )}
          </div>

          {/* 返回上一步 */}
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className={`w-full py-2 text-xs font-medium transition-colors ${
                isLight ? 'text-stone-400 hover:text-stone-900' : 'text-stone-600 hover:text-stone-300'
              }`}
            >
              ← 返回
            </button>
          )}
        </form>

        {/* 底部链接 */}
        <div className={`mt-10 text-center text-xs ${isLight ? 'text-stone-400' : 'text-stone-600'}`}>
          已有账户？{' '}
          <Link to="/login" className={`font-medium transition-colors ${
            isLight ? 'text-stone-900 hover:text-emerald-600' : 'text-stone-300 hover:text-emerald-400'
          }`}>
            登录
          </Link>
        </div>
      </div>

      {/* 底部条款 */}
      <div className={`absolute bottom-8 text-[10px] tracking-wider ${isLight ? 'text-stone-300' : 'text-stone-700'}`}>
        注册即表示同意服务条款和隐私政策
      </div>
    </div>
  );
};

export default RegisterPage;
